import React, { useState } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  InputAdornment,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Login as LoginIcon,
  Google as GoogleIcon,
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { AuthPageLayout } from '../components/AuthPageLayout';

/**
 * 登入表單驗證規則
 */
const validationSchema = Yup.object({
  email: Yup.string()
    .email('Email 格式錯誤')
    .required('請輸入 Email'),
  password: Yup.string()
    .min(6, '密碼長度至少 6 個字元')
    .required('請輸入密碼'),
});

/**
 * 登入頁面組件
 */
export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, signInWithGoogle, isFirebaseEnabled } = useAuth();

  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // 取得重定向目標 (登入前嘗試訪問的頁面)
  const from = (location.state as any)?.from?.pathname || '/dashboard';

  /**
   * Formik 表單管理
   */
  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      setIsLoading(true);
      setError(null);

      try {
        await login(values.email, values.password);
        // 登入成功，重定向
        navigate(from, { replace: true });
      } catch (err: any) {
        console.error('登入失敗:', err);

        // 判斷錯誤類型並提供對應訊息
        let errorMessage = '登入失敗，請檢查 Email 和密碼';

        if (!err.response && err.request) {
          // 網路錯誤：請求已發送但未收到回應（後端未啟動或網路斷線）
          errorMessage = '無法連線伺服器，請確認後端服務是否啟動';
        } else if (err.code === 'ECONNABORTED' || err.message.includes('timeout')) {
          // 請求超時
          errorMessage = '請求超時，請檢查網路連線或稍後再試';
        } else if (err.response?.data?.message) {
          // 後端返回的錯誤訊息
          errorMessage = err.response.data.message;
        } else if (err.message) {
          // 其他錯誤
          errorMessage = err.message;
        }

        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
  });

  /**
   * 處理 Google OAuth 登入
   */
  const handleGoogleLogin = async () => {
    // 檢查 Firebase 是否啟用
    if (!isFirebaseEnabled) {
      setError('Google 登入功能未啟用，請使用 Email 登入');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // 呼叫 Firebase Google OAuth 登入
      await signInWithGoogle();

      // 登入成功，重定向到目標頁面
      navigate(from, { replace: true });
    } catch (err: any) {
      console.error('Google 登入失敗:', err);

      // 處理不同的錯誤情況
      let errorMessage = 'Google 登入失敗';

      if (err.message) {
        errorMessage = err.message;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 切換密碼顯示/隱藏
   */
  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <AuthPageLayout
      pageTitle="紫微斗數算命系統"
      pageSubtitle="登入您的帳號"
    >
      <Paper
        elevation={24}
        sx={{
          p: 4,
          borderRadius: 3,
          background: '#ffffff',
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.12)',
        }}
      >

          {/* 錯誤訊息 */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* 登入表單 */}
          <form onSubmit={formik.handleSubmit}>
            <Box display="flex" flexDirection="column" gap={2}>
              {/* Email 輸入 */}
              <TextField
                fullWidth
                id="email"
                name="email"
                label="Email"
                type="email"
                variant="outlined"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                disabled={isLoading}
                autoComplete="email"
                InputLabelProps={{ shrink: true }}
              />

              {/* 密碼輸入 */}
              <TextField
                fullWidth
                id="password"
                name="password"
                label="密碼"
                type={showPassword ? 'text' : 'password'}
                variant="outlined"
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.password && Boolean(formik.errors.password)}
                helperText={formik.touched.password && formik.errors.password}
                disabled={isLoading}
                autoComplete="current-password"
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleTogglePasswordVisibility}
                        edge="end"
                        disabled={isLoading}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* 登入按鈕 */}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isLoading}
                startIcon={<LoginIcon />}
                sx={{ mt: 1 }}
              >
                {isLoading ? '登入中...' : '登入'}
              </Button>

              {/* 註冊連結 */}
              <Box textAlign="center" mt={2}>
                <Typography variant="body2" color="text.secondary">
                  還沒有帳號？{' '}
                  <Link
                    component={RouterLink}
                    to="/register"
                    underline="hover"
                    fontWeight="bold"
                  >
                    立即註冊
                  </Link>
                </Typography>
              </Box>
            </Box>
          </form>
        </Paper>

        {/* 頁尾資訊 */}
        <Box textAlign="center" mt={3}>
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(0, 0, 0, 0.5)',
            }}
          >
            © 2026 HowYun.Me 我的好運氣 - 紫微斗數算命系統. All rights reserved.
          </Typography>
        </Box>
    </AuthPageLayout>
  );
};

export default LoginPage;
